import { LitElement, css, html } from 'lit';
import { property } from 'lit/decorators.js';
import '../../voy-timeseries-chart/src/voy-timeseries-chart';
import { VoyTimeseriesChart } from '../../voy-timeseries-chart/src/voy-timeseries-chart';
import { ChartMode, ChartsVisibility } from './chart-selection';
import './chart-selection';
import { ParameterType, VesselPerformanceModel } from '../../../src/models/vessel-performance-model';
import { PerformanceUtils } from '../../../src/utils/performance-utils';
import { VesselPlanningModel } from '../../../src/models/vessel-planning-model';
import { extractAllTimestamps, getHardWaypointsInfoFromRoutes, getPilotPointsTimestamps, getPilotPointWaypointNumbers, getWaypointDisplayStartIndex } from '../../../src/utils/route-utils';
import { extractSpotWeatherDataFromRoute } from '../../../src/utils/utils';
import { TooltipType } from '../../voy-timeseries-chart/src/voy-timeseries-chart-tooltip';
import { ChartNames, echartUtils, PilotPointTimestamps, TimeseriesData, TimeseriesValues, TimestampData, WaypointAndSeriesMatching, WaypointTimestamps } from '../../voy-timeseries-chart/src/echartUtils';
import {repeat} from 'lit/directives/repeat.js';
import { debounce, GetFormattedLocalDate } from '../../../src/utils/helpers';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import "../../voyage-tooltip/src/voyage-tooltip-proxy";
import '../../voyage-spinner/src/voyage-spinner';

// Echarts are currently loaded as global
declare const echarts: any;

const performanceTypes = [ChartNames.Sog, ChartNames.Stw, ChartNames.CurrentImpact, ChartNames.Power, ChartNames.Rpm, ChartNames.RollAngle];
const weatherTypes = [ChartNames.Wind50M, ChartNames.Currents, ChartNames.Wave, ChartNames.Swell];
const chartTypes: ChartNames[] = [...performanceTypes, ...weatherTypes];

export class VesselPerformanceFullVoyageExecution extends LitElement {

    @property({type: Boolean}) maximized: boolean = false;
    @property({type: Object, attribute: false}) plan: any = null;

    constructor()
    {
        super();

        this._updatePerfSimulatedData = this._updatePerfSimulatedData.bind(this);
        this._updatePerfActualData = this._updatePerfActualData.bind(this);
        this._onChartOptionsUpdated = this._onChartOptionsUpdated.bind(this);
        this._updateData = this._updateData.bind(this);
        this._debouncedUpdateData = debounce(this._updateData, 200);
        this._extractAllTimestamps = this._extractAllTimestamps.bind(this);
        this._debouncedExtractAllTimestamps = debounce(this._extractAllTimestamps, 200)
        this._onChartSelectionLoaded = this._onChartSelectionLoaded.bind(this);
    }

    static get styles() {
        return [
            css`
            :host {
                display: flex;
                flex-direction: column;
                max-height: calc(100vh - 230px);
                width: 100%;
                overflow-y: scroll;
                pointer-events: auto;
                box-shadow: 1px 1px 5px var(--secondary-voyage-box-shadow);
            }

            .container {
                display: none;
            }

            .container[shown] {
                display: block;
                background: var(--neutrals-grey-10, white);
            }

            .list {
                display: flex;
                flex-direction: column;
                width: 100%;
                height: 100%;
            }

            .divider {
                width: calc(100% - 32px);
                height: 1px;
                background-color: var(--neutrals-grey-06, black);
                border: none;
            }

            .route-info-container {
                background: var(--neutrals-grey-10, white);
                padding: 12px 16px;
            }

            .route-info-item {
                font-size: 12px;
                color: var(--neutrals-grey-03);
            }

            .route-info-label {
                font-weight: 600;
                margin-right: 8px;
            }

            .route-info-detail {
                color: var(--neutrals-grey-04);
            }

            .route-info-loading {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                color: var(--neutrals-grey-04);
            }

            .route-info-spinner {
                display: inline-block;
                width: 12px;
                height: 12px;
            }

            .title {
                color: var(--neutrals-grey-03, black);
                font-size: 14px;
                margin: 10px 10px 0px 16px;
                display: flex;
            }

            .chart {
                display: none;
                flex-direction: row;
                font-family: 'MaerskText';
            }

            voy-timeseries-chart {
                pointer-events: auto;
            }

            .chart[shown] {
                display: flex;
            }

            .question-mark {
                cursor: pointer;
                margin-left: 10px;
            }
        `];
    }

    render() {
        const maxRollAngleValue = Math.max(...this._chartsData['rollAngle'].flatMap(data => 
            data.timeseries.values.filter((v): v is number => v !== null)
        ));

        const maxRollAngleYAxis = this._rollAngleThresholdLimit && this._rollAngleThresholdLimit > maxRollAngleValue 
            ? this._rollAngleThresholdLimit : maxRollAngleValue;

        return html`
            <chart-selection
                @chart-options-updated=${this._onChartOptionsUpdated}
                @chart-selection-loaded=${this._onChartSelectionLoaded}
                .chartsKey=${ChartMode.ExecutionMode}
                .maximized=${this.maximized}
            >
            </chart-selection>
            ${this.plan && this.plan.routes.length > 0 && this.plan.selectedRouteIndex >= 0 ? html`
                <div class="route-info-container">
                    <hr class="divider">
                    ${(() => {
                        const route = this.plan!.routes[this.plan!.selectedRouteIndex];
                        // Show a loading spinner while the route is (re)calculating
                        if (route.recalcNeeded) {
                            return html`
                                <div class="route-info-item">
                                    <span class="route-info-label">Route ${this.plan!.selectedRouteIndex + 1}:</span>
                                    <span class="route-info-loading">
                                        <voyage-spinner class="route-info-spinner"></voyage-spinner>
                                        Calculating…
                                    </span>
                                </div>
                            `;
                        }

                        const forecastVersionRaw = route.route_info?.calculationInput?.forecast_versions?.[0] || '';
                        // Format forecast version: F20260812060000 -> 2026-08-12 06:00
                        let forecastVersion = 'N/A';
                        if (forecastVersionRaw && forecastVersionRaw.startsWith('F') && forecastVersionRaw.length === 15) {
                            const dateStr = forecastVersionRaw.substring(1); // Remove 'F'
                            if (dateStr === '00000000000000') {
                                forecastVersion = 'Climatic';
                            } else {
                                const year = dateStr.substring(0, 4);
                                const month = dateStr.substring(4, 6);
                                const day = dateStr.substring(6, 8);
                                const hour = dateStr.substring(8, 10);
                                const minute = dateStr.substring(10, 12);
                                forecastVersion = `${year}-${month}-${day} ${hour}:${minute}`;
                            }
                        }
                        
                        const timestamp = route.creationTime;
                        const formattedDate = timestamp ? GetFormattedLocalDate(timestamp) : 'N/A';

                        return html`
                            <div class="route-info-item">
                                <span class="route-info-label">Route ${this.plan!.selectedRouteIndex + 1}:</span>
                                <span class="route-info-detail">
                                    Forecast: ${forecastVersion} | Calculated: ${formattedDate}
                                </span>
                            </div>
                        `;
                    })()}
                </div>
            ` : ''}            
            <div class="list">
                ${repeat(chartTypes, (chartType) => {
                    const shown = this._chartsVisibility[(<keyof ChartsVisibility>chartType)];
                    const isPerformance = performanceTypes.includes(chartType);

                    return html`
                        <div class="container" ?shown=${shown}>
                            <hr class="divider">
                            <div class="title">
                                <span>${echartUtils.getChartTitle(chartType)}</span>
                                ${!isPerformance
                                    ? html`
                                        <div class="question-mark">
                                            ${SvgIcons.lightQuestionCircle}
                                            <voyage-tooltip-proxy
                                                text="Historical forecast is not available at this time.<br>Developers are working on delivering this."
                                                html-content
                                                .positionPriority=${["top"]}>
                                            </voyage-tooltip-proxy>
                                        </div>`
                                    : html``
                                }
                            </div>
                            <div class="chart" ?shown=${shown}>
                                <voy-timeseries-chart
                                    id=${`${chartType}chart`}
                                    unit=${echartUtils.getUnit(chartType)}
                                    .data=${shown ? this._chartsData[chartType] : []}
                                    .allTimestamps=${shown ? this._allTimestamps : []}
                                    .pilotPoints=${this._pilotPoints}
                                    .tooltipType=${isPerformance ? TooltipType.standart : TooltipType.weather}
                                    .routesAndWaypoints=${this._hardWaypoints}
                                    .pilotPointWaypointNumbers=${this._pilotPointWaypointNumbers}
                                    .showWaypoints=${this._firstShownChart === chartType}
                                    .waypointAndSeriesMatching=${WaypointAndSeriesMatching.SameWaypointsForAllSeries}
                                    .showLegend=${true}
                                    .yAxisChartParams=${{
                                                        nameRotate: 0,
                                                        nameLocation: 'middle',
                                                        padding: [0,30,0,0],
                                                        nameGap: 10,
                                                        ...(chartType === "rollAngle" && { max: maxRollAngleYAxis * 1.3 })
                                                        
                                                    }}
                                >
                                </voy-timeseries-chart>
                            </div>
                        </div>
                    `
                })}
            </div>
        `;
    }

firstUpdated() {
    VesselPerformanceModel!.addEventListener('performance-updated', this._updatePerfSimulatedData);
    VesselPerformanceModel!.addEventListener('papi-performance-updated', this._updatePerfActualData);
    VesselPlanningModel!.addEventListener('executing-plan-changed', this._debouncedUpdateData);

    this._updatePerfActualData();
    this._updatePerfSimulatedData();
    this._debouncedUpdateData();

    // Wait for the next frame to ensure all charts are rendered
    requestAnimationFrame(() => {
        this.updateComplete.then(() => {
            this._sogChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#sogchart');
            this._stwChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#stwchart');
            this._currentImpactChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#currentImpactchart');
            this._powerChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#powerchart');
            this._rpmChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#rpmchart');
            this._wind50MChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#wind50Mchart');
            this._waveChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#wavechart');
            this._swellChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#swellchart');
            this._currentsChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#currentschart');
            this._rollAngleChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#rollAnglechart');
            
            const charts = [
                this._sogChart, 
                this._stwChart, 
                this._currentImpactChart, 
                this._powerChart, 
                this._rpmChart, 
                this._wind50MChart, 
                this._waveChart, 
                this._swellChart, 
                this._currentsChart,
                this._rollAngleChart
            ].filter(c => c !== null);
            
            VoyTimeseriesChart.connect(charts);
        });
    });
}

    disconnectedCallback()
    {
        super.disconnectedCallback();
        VesselPerformanceModel!.removeEventListener('performance-updated', this._updatePerfSimulatedData);
        VesselPerformanceModel!.removeEventListener('papi-performance-updated', this._updatePerfActualData);
        VesselPlanningModel!.removeEventListener('executing-plan-changed', this._debouncedUpdateData)
    }

    @property({attribute: false}) private _sogData: TimeseriesData[] = [];
    @property({attribute: false}) private _powerData: TimeseriesData[] = [];
    @property({attribute: false}) private _rpmData: TimeseriesData[] = [];
    @property({attribute: false}) private _stwData: TimeseriesData[] = [];
    @property({attribute: false}) private _currentImpactData: TimeseriesData[] = [];
    @property({attribute: false}) private _rollAngleData: TimeseriesData[] = [];
    @property({attribute: false}) private _rollAngleThresholdLimit: number | null = null; 
    @property({attribute: false}) private _pilotPoints: PilotPointTimestamps = {departure: -1, arrival: -1};
    @property({attribute: false}) private _hardWaypoints: WaypointTimestamps[][] = [];
    @property({attribute: false}) private _pilotPointWaypointNumbers: number[][] = [];
    @property({attribute: false}) private _allTimestamps: TimestampData[] = [];
    @property({attribute: false}) private _firstShownChart: keyof ChartsVisibility = ChartNames.Sog;
    private _debouncedUpdateData: () => void;
    private _debouncedExtractAllTimestamps: () => void;
    @property({attribute: false}) private _chartsData: {[index in ChartNames]: TimeseriesData[]} = {
        sog: [],
        power: [],
        rpm: [],
        wind50M: [],
        currents: [],
        wave: [],
        swell: [],
        stw: [],
        currentImpact: [],
        rollAngle: []
    }
    @property({attribute: false}) private _chartsVisibility: ChartsVisibility = {
        sog: true,
        power: false,
        rpm: false,
        wind50M: false,
        currents: false,
        wave: false,
        swell: false,
        stw: false,
        currentImpact: false,
        rollAngle: false
    }
    @property({attribute: false}) private _sogChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _stwChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _currentImpactChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _powerChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _rpmChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _wind50MChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _waveChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _swellChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _currentsChart: VoyTimeseriesChart|null = null;
    @property({attribute: false}) private _rollAngleChart: VoyTimeseriesChart|null = null;

    private _onChartSelectionLoaded(e: CustomEvent) {
        const {options} = e.detail
        this._chartsVisibility = options;
        this._updateFirstShownChart();
    }

    private _onChartOptionsUpdated(e: CustomEvent) {
        const {keyChanged, newValue} = e.detail.state
        this._chartsVisibility[<keyof ChartsVisibility>keyChanged] = newValue;
        this._updateFirstShownChart();
        this.requestUpdate();
    }

    private _extractAllTimestamps() {
        this._allTimestamps = extractAllTimestamps(this._pilotPoints, this._chartsData, this._hardWaypoints);
    }

    private _updateFirstShownChart() {
        for (const key of Object.keys(this._chartsVisibility)) {
            if (this._chartsVisibility[<keyof ChartsVisibility>key]) {
                this._firstShownChart = <keyof ChartsVisibility>key;
                return;
            }
        }
    }
    
    private async _updateData() {
        const plan = await VesselPlanningModel!.getExecutingPlan();
        
        if (!plan) return;

        this.plan = plan;
        this._pilotPoints = getPilotPointsTimestamps(plan);

        if (!plan.routes.length) return;
        const vesselCharacteristics = await VesselPlanningModel!.getVesselCharacteristics();
        const selectedRoutes = [plan.routes[plan.selectedRouteIndex]];
        const waypointDisplayStartIndex = getWaypointDisplayStartIndex(vesselCharacteristics?.ecdisWPStartIndex);
        const waypointNumberingOptions = { numberHardWaypointsOnly: true };
        this._hardWaypoints = getHardWaypointsInfoFromRoutes(selectedRoutes, waypointDisplayStartIndex, waypointNumberingOptions);
        this._pilotPointWaypointNumbers = getPilotPointWaypointNumbers(selectedRoutes, waypointDisplayStartIndex, waypointNumberingOptions);
        const weatherData = extractSpotWeatherDataFromRoute([plan.routes[plan.selectedRouteIndex]], "execution");
        this._chartsData.wind50M = weatherData.wind50M;
        this._chartsData.swell = weatherData.swell;
        this._chartsData.wave = weatherData.wave;
        this._chartsData.currents = weatherData.currents;

        this._debouncedExtractAllTimestamps();
    }

    private _updatePerfSimulatedData() {
        const KmToNM = 1 / 1.852; // Km -> Nautical miles

        try
        {
            const sogs = this._sogData.filter( d => d.name !== "simulated" );
            const powers = this._powerData.filter( d => d.name !== "simulated" );
            const rpms = this._rpmData.filter( d => d.name !== "simulated" );
            const stws = this._stwData.filter( d => d.name !== "simulated" );
            const currentImpacts = this._currentImpactData.filter( d => d.name !== "simulated" );
            const rollAngles = this._rollAngleData.filter( d => d.name !== "simulated" && d.name !== "threshold limit" );

            const sog = VesselPerformanceModel!.extractOneParam('current', ParameterType.SpeedOverGround, true);
            if (sog.length > 0)
            {
                sogs.push({
                    name: "simulated",
                    color: "--performance-viewer-simulated-color",
                    timeseries: PerformanceUtils.paramValuesToTimeseries(sog)
                });
            }
            
            const power = VesselPerformanceModel!.extractOneParam('current', ParameterType.PropulsionPower, true);
            if (power.length > 0)
            {
                const timeseries: TimeseriesValues = PerformanceUtils.paramValuesToTimeseries(power);
                // round values to whole numbers
                timeseries.values = timeseries.values.map(number => number === null ? null : Math.round(number));
                if (timeseries.lower)
                {
                    timeseries.lower = timeseries.lower.map(number => number === null ? null : Math.round(number));
                }
                if (timeseries.upper)
                {
                    timeseries.upper = timeseries.upper.map(number => number === null ? null : Math.round(number));
                }

                powers.push({
                    name: "simulated",
                    color: "--performance-viewer-simulated-color",
                    timeseries: timeseries
                });
            }

            const rpm = VesselPerformanceModel!.extractOneParam('current', ParameterType.RPM, true);
            if (rpm.length > 0)
            {
                rpms.push({
                    name: "simulated",
                    color: "--performance-viewer-simulated-color",
                    timeseries: PerformanceUtils.paramValuesToTimeseries(rpm)
                });
            }

            const stw = VesselPerformanceModel!.extractOneParam('current', ParameterType.SpeedThroughWater, true);
            if (stw.length > 0)
            {
                stws.push({
                    name: "simulated",
                    color: "--performance-viewer-simulated-color",
                    timeseries: PerformanceUtils.paramValuesToTimeseries(stw)
                });
            }

            const currentImpact = VesselPerformanceModel!.extractOneParam('current', ParameterType.CurrentImpact, false);  // Note: false to allow negative values
            if (currentImpact.length > 0)
            {
                currentImpacts.push({
                    name: "simulated",
                    color: "--performance-viewer-simulated-color",
                    timeseries: PerformanceUtils.paramValuesToTimeseries(currentImpact)
                });
            }

            const rollAngle = VesselPerformanceModel!.extractOneParam('current', ParameterType.RollAngle, true);
            if (rollAngle.length > 0)
            {
                const rollAngleTimeseries = PerformanceUtils.paramValuesToTimeseries(rollAngle);

                if (this._rollAngleThresholdLimit !== null)
                    {
                        // Use the rollAngle series timestamps (not _allTimestamps) to avoid
                        // a circular dependency where old timestamps keep polluting _allTimestamps
                        const rollAngleTimestampData: TimestampData[] = rollAngleTimeseries.timestamps.map(ts => ({ timestamp: ts, isWp: false }));
                        rollAngles.push({
                            name: "threshold limit",
                            color: "--performance-viewer-threshold-color",
                            timeseries: PerformanceUtils.addConstantToTimeseries(rollAngleTimestampData, this._rollAngleThresholdLimit)
                        });
                    }
                    
                rollAngles.push({
                    name: "simulated",
                    color: "--performance-viewer-simulated-color",
                    timeseries: rollAngleTimeseries
                });


            }


            this._chartsData.sog = sogs
            this._chartsData.power = powers;
            this._chartsData.rpm = rpms;
            this._chartsData.stw = stws;
            this._chartsData.currentImpact = currentImpacts;
            this._chartsData.rollAngle = rollAngles;
            this._debouncedExtractAllTimestamps();

        } catch (err) {
            console.warn(err);
        }
    }

    private _updatePerfActualData() {
        try {
            const now = Date.now();
            const restrictToPastAndPresent = (values: TimeseriesValues): TimeseriesValues => {
                const filtered: TimeseriesValues = {
                    timestamps: [],
                    values: []
                };

                if (values.lower) {
                    filtered.lower = [];
                }
                if (values.upper) {
                    filtered.upper = [];
                }

                for (let i = 0; i < values.timestamps.length; i++) {
                    if (values.timestamps[i] > now) {
                        continue;
                    }

                    filtered.timestamps.push(values.timestamps[i]);
                    filtered.values.push(values.values[i]);

                    if (filtered.lower && values.lower) {
                        filtered.lower.push(values.lower[i]);
                    }
                    if (filtered.upper && values.upper) {
                        filtered.upper.push(values.upper[i]);
                    }
                }

                return filtered;
            };

            const sogs = this._sogData.filter( d => d.name !== "actual" );
            const powers = this._powerData.filter( d => d.name !== "actual" );
            const rpms = this._rpmData.filter( d => d.name !== "actual" );
            const stws = this._stwData.filter( d => d.name !== "actual" );

            const sog = VesselPerformanceModel!.extractOnePAPIParam(ParameterType.SpeedOverGround, true);
            if (sog.length > 0)
            {
                sogs.push({
                    name: "actual",
                    fillArea: true,
                    color: "--performance-viewer-actual-color",
                    timeseries: restrictToPastAndPresent(PerformanceUtils.paramValuesToTimeseries(sog))
                });
                
            }
            
            const power = VesselPerformanceModel!.extractOnePAPIParam(ParameterType.PropulsionPower, true);
            if (power.length > 0)
            {
                const timeseries: TimeseriesValues = PerformanceUtils.paramValuesToTimeseries(power);
                // round values to whole numbers
                timeseries.values = timeseries.values.map(number => number === null ? null : Math.round(number));
                
                powers.push({
                    name: "actual",
                    fillArea: true,
                    color: "--performance-viewer-actual-color",
                    timeseries: restrictToPastAndPresent(timeseries)
                });
            }

            const rpm = VesselPerformanceModel!.extractOnePAPIParam(ParameterType.RPM, true);
            if (rpm.length > 0)
            {
                rpms.push({
                    name: "actual",
                    fillArea: true,
                    color: "--performance-viewer-actual-color",
                    timeseries: restrictToPastAndPresent(PerformanceUtils.paramValuesToTimeseries(rpm))
                });
            }

            const stw = VesselPerformanceModel!.extractOnePAPIParam(ParameterType.SpeedThroughWater, true);
            if (stw.length > 0)
            {
                stws.push({
                    name: "actual",
                    fillArea: true,
                    color: "--performance-viewer-actual-color",
                    timeseries: restrictToPastAndPresent(PerformanceUtils.paramValuesToTimeseries(stw))
                });
                
            }

            this._sogData = sogs;
            this._powerData = powers;
            this._rpmData = rpms;
            this._stwData = stws;

            this._chartsData.sog = sogs;
            this._chartsData.power = powers;
            this._chartsData.rpm = rpms;
            this._chartsData.stw = stws;
            this._debouncedExtractAllTimestamps();
        } catch (err) {
            console.warn(err);
        }
    }
};

customElements.define('vessel-performance-full-voyage-execution', VesselPerformanceFullVoyageExecution);
